import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import { api } from "../lib/api";
import { Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function PaymentSuccess() {
  const query = useQuery();
  const sessionId = query.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "checking", message: "Vérification du paiement…" });
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "Session de paiement manquante." });
      return;
    }
    const poll = async () => {
      if (attemptsRef.current >= 10) {
        setState({ status: "timeout", message: "Le paiement prend plus de temps que prévu. Vérifiez votre email pour la confirmation." });
        return;
      }
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/session/${sessionId}/status`);
        if (data.payment_status === "paid" && data.booking_id) {
          setState({ status: "paid", message: "Paiement confirmé !" });
          setTimeout(() => navigate(`/confirmation/${data.booking_id}`, { replace: true }), 800);
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", message: "La session a expiré. Veuillez réessayer." });
          return;
        }
        setState({ status: "checking", message: "Paiement en cours de traitement…" });
        timerRef.current = setTimeout(poll, 2000);
      } catch (e) {
        setState({ status: "error", message: e.response?.data?.detail || "Erreur de vérification." });
      }
    };
    poll();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicNavbar />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16">
        <div className="bg-white rounded-3xl border border-slate-200 p-10 text-center" data-testid="payment-success-card">
          {state.status === "checking" && (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
              <h1 className="font-heading text-2xl font-medium tracking-tight text-slate-900 mt-6">Vérification du paiement</h1>
              <p className="text-sm text-slate-500 mt-2">{state.message}</p>
            </>
          )}
          {state.status === "paid" && (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-violet-600 text-white flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <h1 className="font-heading text-2xl font-medium tracking-tight text-slate-900 mt-6">Paiement confirmé</h1>
              <p className="text-sm text-slate-500 mt-2">Nous préparons votre confirmation de réservation…</p>
            </>
          )}
          {(state.status === "expired" || state.status === "error" || state.status === "timeout") && (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
                <AlertTriangle className="h-8 w-8" />
              </div>
              <h1 className="font-heading text-2xl font-medium tracking-tight text-slate-900 mt-6">Paiement non confirmé</h1>
              <p className="text-sm text-slate-500 mt-2">{state.message}</p>
              <Link to="/" className="inline-flex mt-6 items-center gap-2 px-6 py-3 rounded-full btn-primary text-sm font-semibold">
                Retour à l'accueil
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-slate-50">
      <PublicNavbar />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16">
        <div className="bg-white rounded-3xl border border-slate-200 p-10 text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center">
            <XCircle className="h-8 w-8" />
          </div>
          <h1 className="font-heading text-2xl font-medium tracking-tight text-slate-900 mt-6">Paiement annulé</h1>
          <p className="text-sm text-slate-500 mt-2">Aucun montant n'a été débité. Vous pouvez reprendre votre réservation à tout moment.</p>
          <Link to="/" className="inline-flex mt-6 items-center gap-2 px-6 py-3 rounded-full btn-primary text-sm font-semibold">
            Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
}
